import {
	Box,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import { httpClient } from "api/httpClient";
import { LoadingSpinner } from "components/LoadingSpinner";
import { LabelDocumentTemplateTypes } from "entities/DocumentTemplateType";
import React, { useEffect, useState } from "react";
import { useNotifyError } from "utils/notifiers/useNotifyError";

const TemplateInfoPreviewButton = ({ onInfoButtonClick, translate }) => (
	<Tooltip title={translate("osfc.buttons.info")} placement="top">
		<IconButton aria-label="Info" size="small" onClick={onInfoButtonClick}>
			<InfoIcon color="secondary" />
		</IconButton>
	</Tooltip>
);

const TemplateInfoPreviewDialog = ({
	documentTemplateType,
	onInfoDialogClose,
	translate,
	companyId,
}) => {
	const notifyError = useNotifyError();

	const [documentTemplateVariablesTranslations, setDocumentTemplateVariablesTranslations] =
		useState(undefined);
	const [loading, setLoading] = useState(undefined);

	useEffect(() => {
		const fetchVariables = () => {
			setLoading(true);
			const queryString = `filter=${encodeURIComponent(JSON.stringify({ company_id: companyId }))}`;
			httpClient(`/document-templates-variables/${documentTemplateType}?${queryString}`, {
				method: "GET",
			})
				.then(({ json }) => {
					setDocumentTemplateVariablesTranslations(json);
				})
				.catch((error) => {
					notifyError(error);
				})
				.finally(() => {
					setLoading(false);
				});
		};
		fetchVariables();
	}, []);

	const isLabelTemplate = LabelDocumentTemplateTypes.includes(documentTemplateType);

	return (
		<Dialog open maxWidth="md" onClose={onInfoDialogClose}>
			<DialogTitle>{translate("osfc.dialogs.document-templates.info.title")}</DialogTitle>
			<DialogContent>
				{!loading && documentTemplateVariablesTranslations !== undefined ? (
					<React.Fragment>
						<Typography>
							{translate("osfc.dialogs.document-templates.info.description")}{" "}
							<span style={{ fontWeight: "bold" }}>
								{"{"}the_variable_name{"}"}
							</span>
						</Typography>
						{isLabelTemplate && (
							<Box mt={2} mb={2}>
								<Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
									{translate("osfc.dialogs.document-templates.info.extended-syntax.title")}
								</Typography>
								<Box mt={1}>
									<Typography variant="body2">
										{translate("osfc.dialogs.document-templates.info.extended-syntax.slicing")}
									</Typography>
									<Box ml={2} mt={0.5}>
										<Typography variant="body2" component="div">
											• <code>{"{sscc[:-4]}"}</code> - {translate("osfc.dialogs.document-templates.info.extended-syntax.slicing-example-1")}
										</Typography>
										<Typography variant="body2" component="div">
											• <code>{"{sscc[-4:]}"}</code> - {translate("osfc.dialogs.document-templates.info.extended-syntax.slicing-example-2")}
										</Typography>
										<Typography variant="body2" component="div">
											• <code>{"{sscc[0:14]}"}</code> - {translate("osfc.dialogs.document-templates.info.extended-syntax.slicing-example-3")}
										</Typography>
									</Box>
								</Box>
								<Box mt={1}>
									<Typography variant="body2">
										{translate("osfc.dialogs.document-templates.info.extended-syntax.image-params")}
									</Typography>
									<Box ml={2} mt={0.5}>
										<Typography variant="body2" component="div">
											• <code>{"{barcode_pallet_sscc@w=1.5,h=2.0}"}</code> - {translate("osfc.dialogs.document-templates.info.extended-syntax.barcode-example")}
										</Typography>
										<Typography variant="body2" component="div">
											• <code>{"{qr_code_pallet_sscc@s=0.5}"}</code> - {translate("osfc.dialogs.document-templates.info.extended-syntax.scale-example")}
										</Typography>
									</Box>
								</Box>
								<Box mt={1}>
									<Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
										{translate("osfc.dialogs.document-templates.info.extended-syntax.spacing-note")}
									</Typography>
								</Box>
							</Box>
						)}
						<Box mt={2}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell variant="head" style={{ fontWeight: "bold" }}>
											{translate("osfc.dialogs.document-templates.info.header.variable")}
										</TableCell>
										<TableCell variant="head" style={{ fontWeight: "bold" }}>
											{translate("osfc.dialogs.document-templates.info.header.description")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{Object.entries(documentTemplateVariablesTranslations).map(
										([varName, desc], index) => (
											<TableRow key={index}>
												<TableCell variant="body" style={{ fontWeight: "bold" }}>
													{varName}
												</TableCell>
												<TableCell variant="body">{translate(desc)}</TableCell>
											</TableRow>
										)
									)}
								</TableBody>
							</Table>
						</Box>
					</React.Fragment>
				) : (
					<LoadingSpinner />
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onInfoDialogClose} color="primary">
					{translate("ra.action.cancel")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export { TemplateInfoPreviewButton, TemplateInfoPreviewDialog };
